import { useCallback, useEffect, useRef, useState } from 'react';
import { TypewriterText } from '@/components';

import styles from './TraceHistoryMessages.module.sass';

export type TraceHistoryMessage = {
  idx: number;
  message: string;
};

export interface TraceHistoryMessagesProps {
  messages: TraceHistoryMessage[];
}

export const TraceHistoryMessages = ({
  messages,
}: TraceHistoryMessagesProps) => {
  const incomingMessages = messages;
  const [displayedCount, setDisplayedCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleTypewriterComplete = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setDisplayedCount((prev) => prev + 1);
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [displayedCount]);

  const completedMessages = incomingMessages.slice(0, displayedCount);
  const currentMessage = incomingMessages[displayedCount];

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.title}>Investigating</div>

        <div className={styles.messages}>
          <div ref={scrollRef} className={styles.scroll}>
            <div className={styles.list}>
              {completedMessages.map((message, index) => (
                <Message
                  key={index}
                  number={index + 1}
                  message={message.message}
                />
              ))}
              {currentMessage && (
                <Message
                  key={displayedCount}
                  number={displayedCount + 1}
                  message={currentMessage.message}
                  typewriter
                  onTypewriterComplete={handleTypewriterComplete}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MessageProps {
  number: number;
  message: string;
  typewriter?: boolean;
  onTypewriterComplete?: () => void;
}

const Message = ({
  number,
  message,
  typewriter,
  onTypewriterComplete,
}: MessageProps) => {
  const formattedNumber = number.toString().padStart(3, '0');

  return (
    <div className={styles.message}>
      <div className={styles.number}>{formattedNumber}</div>
      <div className={styles.text}>
        {typewriter && onTypewriterComplete ? (
          <TypewriterText text={message} onComplete={onTypewriterComplete} />
        ) : (
          message
        )}
      </div>
    </div>
  );
};
